// Rendering layer control (React Router)
import React, { Component, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
// BrowserRouter tells React Router how to behave
// Route is a React Component that sets up a rule and displays the right component
import { BrowserRouter, Route, Routes } from 'react-router-dom';
// connect helper allows the App component to access the redux store
import { connect } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline'; // CSS Baseline from material-ui
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import { fetchAuthUser, initializeTheme } from '../actions'; // import all the action creators
import { lightTheme, darkTheme } from './appjscomponents/customMuiTheme';
import ScrollToTop from './appjscomponents/ScrollToTop';
import PrivateRoute from './appjscomponents/PrivateRoute';
import SmallScreenCheck from './appjscomponents/SmallScreenCheck';
import ErrorBoundary from './appjscomponents/ErrorBoundary';
import { fetchAuthUserType, unsubscribeFromAuthType } from '../types';

import './App.css';

// import all the components in
import Header from './header/Header';
import Footer from './footer/Footer';
import ModalManager from './modals/ModalManager';
import SnackbarManager from './snackbars/SnackbarManager';
import CircularLoading from './commoncomponents/CircularLoading';
// VERY IMPORTANT: DO NOT lazy load EditPromptPage or PromptPage will not display
// the prompt correctly. Probably because draft-js and/or TextEditorReadOnly does not load
import EditPromptPage from './editpromptpage/EditPromptPage'; // DO NOT LAZY LOAD EditPromptPage because it would mess with prompt's play on PromptPage!
// VERY IMPORTANT: DO NOT lazy load FrontPage or google bot will not be able to see the prompts
import FrontPage from './frontpage/FrontPage';
// Removed PromptPage from lazyloading / code splitting because it might be causing
// Google to NOT index prompt pages. Note that TagPage and UserPage are still lazy loaded!
// We are NOT sure if it'll impact it!
import PromptPage from './promptpage/PromptPage';

const UserPage = lazy(() => import('./userpage/UserPage'));
const TagPage = lazy(() => import('./tagpage/TagPage'));
const TagsPage = lazy(() => import('./tagspage/TagsPage'));
const SettingsPage = lazy(() => import('./settingspage/SettingsPage'));
const ProfilePage = lazy(() => import('./profilepage/ProfilePage'));
const ConversationsPage = lazy(() =>
  import('./conversationspage/ConversationsPage')
);
const ConversationPage = lazy(() =>
  import('./conversationpage/ConversationPage')
);
const InboxPage = lazy(() => import('./inboxpage/InboxPage'));
const GuidePage = lazy(() => import('./footerpages/GuidePage'));
const RulesPage = lazy(() => import('./footerpages/RulesPage'));
const ContactPage = lazy(() => import('./footerpages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./footerpages/PrivacyPolicyPage'));
const LoginPage = lazy(() => import('./loginpage/LoginPage'));
const SignUpPage = lazy(() => import('./signuppage/SignUpPage'));
const ForgotPasswordPage = lazy(() =>
  import('./forgotpasswordpage/ForgotPasswordPage')
);
const AdminPage = lazy(() => import('./adminpage/AdminPage'));
const NotFoundPage = lazy(() => import('./notfoundpage/NotFoundPage'));

class App extends Component {
  componentDidMount = () => {
    // this.unsubscribeFromAuth = await this.props.fetchAuthUser();
    this.props.fetchAuthUser();
    this.props.initializeTheme();
  };

  componentWillUnmount = () => {
    this.props.unsubscribeFromAuth();
  };

  render() {
    // exact is equivalent to exact={true}, makes the path match exactly
    // because <Header /> is above all the routes, it will always be visible
    const { theme } = this.props;
    const currentTheme = theme === 'dark' ? darkTheme : lightTheme;

    return (
      <ErrorBoundary>
        <BrowserRouter>
          <ScrollToTop>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={currentTheme}>
                <StylesThemeProvider theme={currentTheme}>
                  <div className={theme === 'dark' ? 'App darkMode' : 'App'}>
                    <CssBaseline />
                    <a href="#main-content" className="skip-link">
                      Skip to main content
                    </a>
                    <ModalManager />
                    <SnackbarManager />
                    <Header />
                    <SmallScreenCheck />
                    <main id="main-content" tabIndex={-1}>
                      <Suspense fallback={<CircularLoading />}>
                        <Routes>
                          <Route path="/" element={<FrontPage />} />
                          <Route
                            path="/prompt/:promptID/:promptTitleUri"
                            element={<PromptPage />}
                          />
                          <Route path="/user/:username" element={<UserPage />} />
                          <Route
                            path="/settings/*"
                            element={<PrivateRoute><SettingsPage /></PrivateRoute>}
                          />
                          <Route
                            path="/profile/*"
                            element={<PrivateRoute><ProfilePage /></PrivateRoute>}
                          />
                          <Route path="/tag/:tagID" element={<TagPage />} />
                          <Route path="/tags" element={<TagsPage />} />
                          <Route
                            path="/conversations"
                            element={<ConversationsPage />}
                          />
                          <Route
                            path="/conversation/:conversationID"
                            element={<ConversationPage />}
                          />
                          <Route path="/inbox/*" element={<InboxPage />} />
                          <Route path="/create" element={<EditPromptPage />} />
                          <Route path="/edit/:promptID" element={<EditPromptPage />} />
                          {/* /* so /guide/getting-started and /guide/faqs reach
                              GuidePage, which switches tabs on location.pathname */}
                          <Route path="/guide/*" element={<GuidePage />} />
                          <Route path="/rules" element={<RulesPage />} />
                          <Route path="/contact" element={<ContactPage />} />
                          <Route
                            path="/privacypolicy"
                            element={<PrivacyPolicyPage />}
                          />
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/signup" element={<SignUpPage />} />
                          <Route
                            path="/forgotpassword"
                            element={<ForgotPasswordPage />}
                          />
                          <Route path="/admin/*" element={<AdminPage />} />
                          <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </Suspense>
                    </main>
                    <Footer />
                  </div>
                </StylesThemeProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </ScrollToTop>
        </BrowserRouter>
      </ErrorBoundary>
    );
  }
}

App.propTypes = {
  fetchAuthUser: fetchAuthUserType.isRequired,
  unsubscribeFromAuth: unsubscribeFromAuthType,
  initializeTheme: PropTypes.func.isRequired,
  theme: PropTypes.string
};

App.defaultProps = {
  unsubscribeFromAuth: null,
  theme: 'light'
};

const mapStateToProps = ({ auth: { unsubscribeFromAuth }, theme: { theme } }) => ({
  unsubscribeFromAuth,
  theme
});

// use connect to allow App to access Action Creators
// the 2nd parameter for connect is mapDispatchToProps
// actions are assigned to App as props
export default connect(
  mapStateToProps,
  { fetchAuthUser, initializeTheme }
)(App);
